import { Suspense, lazy } from "react"

import { Toaster } from "react-hot-toast"
import { BiErrorCircle } from "react-icons/bi"
import { IoMdCheckmarkCircleOutline } from "react-icons/io"
import { Navigate, Route, Routes } from "react-router-dom"

import { appConfig } from "./config"

import LogoIcon from "./components/common/LogoIcon"
import RequireAuth from "./components/common/RequireAuth"
import RequireVerified from "./components/common/RequireVerified"
import ScrollTop from "./components/common/ScrollTop"
import { ThemeProvider } from "styled-components"

// Keep layout components as regular imports to preserve navbar/layout
import AppHome from "./screens/application"
import AuthHome from "./screens/authentication"
import AuthContainer from "./screens/authentication/AuthContainer"

import "./App.less"

// Lazy load page components for code splitting
const Home = lazy(() => import("./screens/landing/Home"))
const Register = lazy(() => import("./screens/authentication/Register"))
const Login = lazy(() => import("./screens/authentication/Login"))
const Settings = lazy(() => import("./screens/application/settings"))
const Contact = lazy(() => import("./screens/landing/Contact"))
const Logout = lazy(() => import("./screens/application/Logout"))
const PriceEstimator = lazy(() => import("./screens/application/PriceEstimator"))
const TripList = lazy(() => import("./screens/application/TripList"))
const RequestTrip = lazy(() => import("./screens/application/RequestTrip"))
const ForgotPassword = lazy(() => import("./screens/authentication/ForgotPassword"))
const ResetPassword = lazy(() => import("./screens/authentication/ResetPassword"))
const AuthEstimator = lazy(() => import("./screens/authentication/AuthEstimator"))
const OngoingTrips = lazy(() => import("./screens/application/OngoingTrips"))
const AboutUs = lazy(() => import("./screens/landing/AboutUs"))
const FAQs = lazy(() => import("./screens/landing/FAQs"))
const Blogs = lazy(() => import("./screens/landing/Blogs"))
const TrackTrip = lazy(() => import("./screens/landing/TrackTrip"))
const BlogDetail = lazy(() => import("./screens/landing/BlogDetail"))
const PartnerWithUs = lazy(() => import("./screens/landing/PartnerWithUs"))
const Quote = lazy(() => import("./components/partner/Quote"))
const TermsOfUse = lazy(() => import("./screens/landing/TermsOfUse"))
const PrivacyPolicy = lazy(() => import("./screens/landing/PrivaryPolicy"))
const SlAgreement = lazy(() => import("./screens/landing/SlAgreement"))
const PendingBids = lazy(() => import("./screens/application/PendingBids"))
const Campaign = lazy(() => import("./screens/landing/Campaign"))
const DraftTrips = lazy(() => import("./screens/application/DraftTrips"))
const VerifyAccount = lazy(() => import("./screens/authentication/VerifyAccount"))

const theme = {
   colors: {
      primary: "#e27626",
      secondary: "#344054",
      first: "#fff5ee",
      second: "#65ffda",
      textOne: "#373737",
   },
}

const loadingFallback = (
   <div
      style={{
         display: "flex",
         flexDirection: "column",
         justifyContent: "center",
         alignItems: "center",
         height: "80vh",
         backgroundColor: "#fff",
      }}
   >
      <LogoIcon />
   </div>
)

const toastOptions = {
   className: "",
   duration: 5000,
   style: {},
   success: {
      icon: <IoMdCheckmarkCircleOutline size={20} />,
      style: {
         background: "#1d8217",
         color: "#fff",
         border: "1px solid #1d8217",
      },
   },
   error: {
      icon: <BiErrorCircle size={20} />,
      style: {
         background: "#e61920",
         color: "#fff",
         border: "1px solid #e61920",
      },
   },
}

function App() {
   return (
      <ThemeProvider theme={theme}>
         <Routes>
            <Route
               path="/app"
               element={
                  <RequireAuth>
                     <RequireVerified>
                        <AppHome />
                     </RequireVerified>
                  </RequireAuth>
               }
            >
               <Route index element={<Navigate to={"request"} />} />
               <Route
                  path="request"
                  element={
                     <Suspense fallback={loadingFallback}>
                        <RequestTrip />
                     </Suspense>
                  }
               />
               <Route
                  path="ongoing-trips"
                  element={
                     <Suspense fallback={loadingFallback}>
                        <OngoingTrips />
                     </Suspense>
                  }
               />
               <Route
                  path="settings"
                  element={
                     <Suspense fallback={loadingFallback}>
                        <Settings />
                     </Suspense>
                  }
               />
               <Route
                  path="price-estimator"
                  element={
                     <Suspense fallback={loadingFallback}>
                        <PriceEstimator />
                     </Suspense>
                  }
               />
               {appConfig.features.bidding && (
                  <Route
                     path="pending-bids"
                     element={
                        <Suspense fallback={loadingFallback}>
                           <PendingBids />
                        </Suspense>
                     }
                  />
               )}
               {appConfig.features.saveAsDraft && (
                  <Route
                     path="trips/drafts"
                     element={
                        <Suspense fallback={loadingFallback}>
                           <DraftTrips />
                        </Suspense>
                     }
                  />
               )}
               <Route
                  path="trips/:category"
                  element={
                     <Suspense fallback={loadingFallback}>
                        <TripList />
                     </Suspense>
                  }
               />
            </Route>

            <Route element={<AuthContainer />}>
               <Route
                  path="verify"
                  element={
                     <Suspense fallback={loadingFallback}>
                        <VerifyAccount />
                     </Suspense>
                  }
               />
               <Route
                  path="login"
                  element={
                     <Suspense fallback={loadingFallback}>
                        <Login />
                     </Suspense>
                  }
               />
               <Route
                  path="signup"
                  element={
                     <Suspense fallback={loadingFallback}>
                        <Register />
                     </Suspense>
                  }
               />
               <Route
                  path="forgot-password"
                  element={
                     <Suspense fallback={loadingFallback}>
                        <ForgotPassword />
                     </Suspense>
                  }
               />
               <Route
                  path="pwreset"
                  element={
                     <Suspense fallback={loadingFallback}>
                        <ResetPassword />
                     </Suspense>
                  }
               />
            </Route>
            <Route
               path="logout"
               element={
                  <Suspense fallback={loadingFallback}>
                     <Logout />
                  </Suspense>
               }
            />
            <Route
               path="campaign"
               element={
                  <Suspense fallback={loadingFallback}>
                     <Campaign />
                  </Suspense>
               }
            />

            <Route path={"/"} element={<AuthHome />}>
               <Route
                  path="/"
                  element={
                     <Suspense fallback={loadingFallback}>
                        <Home />
                     </Suspense>
                  }
               />
               <Route
                  path="about-us"
                  element={
                     <Suspense fallback={loadingFallback}>
                        <AboutUs />
                     </Suspense>
                  }
               />
               <Route
                  path="faqs"
                  element={
                     <Suspense fallback={loadingFallback}>
                        <FAQs />
                     </Suspense>
                  }
               />
               <Route
                  path="blogs"
                  element={
                     <Suspense fallback={loadingFallback}>
                        <Blogs />
                     </Suspense>
                  }
               />
               <Route
                  path="blogs/:id"
                  element={
                     <Suspense fallback={loadingFallback}>
                        <BlogDetail />
                     </Suspense>
                  }
               />
               <Route
                  path="price-estimator"
                  element={
                     <Suspense fallback={loadingFallback}>
                        <AuthEstimator />
                     </Suspense>
                  }
               />
               <Route
                  path={"contact"}
                  element={
                     <Suspense fallback={loadingFallback}>
                        <Contact />
                     </Suspense>
                  }
               />
               <Route
                  path={"partner"}
                  element={
                     <Suspense fallback={loadingFallback}>
                        <PartnerWithUs />
                     </Suspense>
                  }
               />
               <Route
                  path={"quote"}
                  element={
                     <Suspense fallback={loadingFallback}>
                        <Quote />
                     </Suspense>
                  }
               />
               <Route
                  path={"policy"}
                  element={
                     <Suspense fallback={loadingFallback}>
                        <PrivacyPolicy />
                     </Suspense>
                  }
               />
               <Route
                  path={"terms"}
                  element={
                     <Suspense fallback={loadingFallback}>
                        <TermsOfUse />
                     </Suspense>
                  }
               />
               <Route
                  path={"sla"}
                  element={
                     <Suspense fallback={loadingFallback}>
                        <SlAgreement />
                     </Suspense>
                  }
               />
               <Route
                  path={"track/:id"}
                  element={
                     <Suspense fallback={loadingFallback}>
                        <TrackTrip />
                     </Suspense>
                  }
               />
            </Route>
         </Routes>
         <ScrollTop />
         <Toaster position="bottom-center" toastOptions={toastOptions} />
      </ThemeProvider>
   )
}

export default App
